/**
 * Weekly progress report for grown-ups (WOW · parents). Pure: the client passes the profile's
 * progress, its journal, a small per-day activity diary it keeps locally, and the skill XP it
 * snapshotted at the start of the week.
 */
import type { PathConfig, PathId, SkillId } from './content/model';
import { SKILL_IDS } from './content/model';
import { pathFraction } from './engine';
import { dayKey, skillLevel, type ProgressState } from './progress';

/** One local day of activity (seconds in the app with this profile, lessons finished, photos taken). */
export interface DiaryDay {
  sec: number;
  lessons: number;
  captures: number;
}

export interface ReportJournalItem {
  createdAt: string;
  skill?: SkillId;
  source: string;
  imageKey?: string;
}

export interface WeeklyReportInput {
  progress: ProgressState;
  journal: readonly ReportJournalItem[];
  diary: Readonly<Record<string, DiaryDay>>;
  /** Skill XP at the start of this week (missing → treat the current XP as the baseline). */
  skillSnapshot?: Partial<Record<SkillId, number>>;
  paths: readonly PathConfig[];
  now: Date;
}

export interface SkillGrowth {
  skill: SkillId;
  xp: number;
  gained: number;
  level: number;
  levelBefore: number;
}

export interface WeeklyReport {
  /** Local day keys, Monday-based week containing `now`. */
  weekStart: string;
  weekEnd: string;
  /** Mon..Sun activity minutes (for the little bar chart). */
  days: { day: string; minutes: number; drawings: number }[];
  drawings: number;
  drawingsLastWeek: number;
  minutes: number;
  lessons: number;
  activeDays: number;
  skills: SkillGrowth[];
  /** The skill that grew most this week (null when none grew). */
  topSkill: SkillId | null;
  /** Colouring progress of each world map (share of its lessons done). */
  worlds: { path: PathId; fraction: number }[];
  totalDrawings: number;
}

/** Monday 00:00 (local) of the week containing `d`. */
export function weekStartOf(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (x.getDay() + 6) % 7; // Mon = 0
  x.setDate(x.getDate() - dow);
  return x;
}

/** Stable key for a week (its Monday's day key). */
export const weekKey = (d: Date): string => dayKey(weekStartOf(d));

export function buildWeeklyReport(input: WeeklyReportInput): WeeklyReport {
  const { progress, journal, diary, now } = input;
  const start = weekStartOf(now);
  const days: WeeklyReport['days'] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    days.push({ day: dayKey(d), minutes: 0, drawings: 0 });
  }
  const inWeek = new Set(days.map((d) => d.day));
  const prevStart = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 7);
  const prevKeys = new Set(Array.from({ length: 7 }, (_, i) => dayKey(new Date(prevStart.getFullYear(), prevStart.getMonth(), prevStart.getDate() + i))));

  let drawings = 0;
  let drawingsLastWeek = 0;
  for (const j of journal) {
    if (j.source === 'calibration') continue;
    const k = dayKey(new Date(j.createdAt));
    if (inWeek.has(k)) {
      drawings++;
      const d = days.find((x) => x.day === k);
      if (d) d.drawings++;
    } else if (prevKeys.has(k)) drawingsLastWeek++;
  }
  let lessons = 0;
  for (const d of days) {
    const e = diary[d.day];
    if (!e) continue;
    d.minutes = Math.round(e.sec / 60);
    lessons += e.lessons;
  }
  const minutes = days.reduce((n, d) => n + d.minutes, 0);
  const activeDays = new Set([...progress.activeDays.filter((d) => inWeek.has(d)), ...days.filter((d) => d.minutes > 0 || d.drawings > 0).map((d) => d.day)]).size;

  const snap = input.skillSnapshot ?? progress.skillXp;
  const skills: SkillGrowth[] = SKILL_IDS.map((skill) => {
    const xp = progress.skillXp[skill] ?? 0;
    const before = Math.min(xp, snap[skill] ?? 0);
    return { skill, xp, gained: xp - before, level: skillLevel(xp).level, levelBefore: skillLevel(before).level };
  })
    .filter((s) => s.xp > 0)
    .sort((a, b) => b.gained - a.gained || b.xp - a.xp);
  const topSkill = skills[0] && skills[0].gained > 0 ? skills[0].skill : null;

  return {
    weekStart: days[0]!.day,
    weekEnd: days[6]!.day,
    days,
    drawings,
    drawingsLastWeek,
    minutes,
    lessons,
    activeDays,
    skills,
    topSkill,
    worlds: input.paths.map((p) => ({ path: p.id, fraction: pathFraction(progress, p) })),
    totalDrawings: journal.filter((j) => j.source !== 'calibration').length,
  };
}
